import React from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const CalculationSummary = ({
  children,
  steps,
  total,
  title = "Calculation Summary",
  subtitle,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isNegative = (amount) =>
    typeof amount === "string" && amount.includes("(");

  if ((!steps || steps.length === 0) && !total && !children) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
          border: "2px dashed",
          borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
          borderRadius: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${
              theme.palette.info.main
            }, ${alpha(theme.palette.info.main, 0.6)})`,
          },
        }}
      >
        <Typography
          variant='h6'
          color='info.main'
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          📊 No Calculation Data Available
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Add calculation steps or a total to see the summary breakdown.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        border: "2px solid",
        borderColor: alpha(theme.palette.primary.main, 0.2),
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        mb: 4,
        position: "relative",
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          p: 4,
          borderBottom: steps ? "2px solid" : "none",
          borderColor: alpha(theme.palette.primary.main, 0.2),
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ mb: subtitle ? 1 : 0 }}
        >
          <Typography variant='h5' fontWeight={700} color='primary.main'>
            {title}
          </Typography>
        </Stack>
        {subtitle && (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ fontWeight: 500 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Enhanced Steps List */}
      {steps && steps.length > 0 && (
        <Box sx={{ px: 4, py: 3 }}>
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 2.5,
                px: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.5),
                mb: index === steps.length - 1 ? 0 : 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "primary.main",
                  }}
                >
                  {index + 1}
                </Box>
                <Tooltip
                  title={step.tooltip || ""}
                  arrow
                  placement='top'
                  sx={{
                    "& .MuiTooltip-tooltip": {
                      maxWidth: 350,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "2px solid",
                      borderColor: "primary.main",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      p: 2,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      cursor: step.tooltip ? "help" : "default",
                      fontSize: isSmallScreen ? "1rem" : "1.1rem",
                    }}
                  >
                    {step.section}
                  </Typography>
                </Tooltip>
              </Box>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  color: isNegative(step.amount)
                    ? "error.main"
                    : "primary.main",
                  fontSize: isSmallScreen ? "1rem" : "1.2rem",
                }}
              >
                {step.amount}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Enhanced Total Section */}
      <Box
        sx={{
          mt: steps && steps.length > 0 ? 3 : 0,
          pt: steps && steps.length > 0 ? 3 : 0,
          borderTop: steps && steps.length > 0 ? "3px solid" : "none",
          borderColor: alpha(theme.palette.primary.main, 0.3),
          px: 4,
          pb: 4,
        }}
      >
        {total ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              p: 4,
              borderRadius: 3,
              border: "2px solid",
              borderColor: alpha(theme.palette.primary.main, 0.3),
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AccountBalanceWalletIcon color='primary' />
              <Tooltip
                title={total.tooltip || ""}
                arrow
                placement='top'
                sx={{
                  "& .MuiTooltip-tooltip": {
                    maxWidth: 400,
                    bgcolor: "background.paper",
                    color: "text.primary",
                    border: "2px solid",
                    borderColor: "primary.main",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    p: 2,
                    fontSize: "0.9rem",
                  },
                }}
              >
                <Typography
                  variant='h4'
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    cursor: total.tooltip ? "help" : "default",
                    fontSize: isSmallScreen ? "1.3rem" : "1.6rem",
                  }}
                >
                  {total.title}
                </Typography>
              </Tooltip>
            </Box>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                color: "primary.main",
                fontSize: isSmallScreen ? "1.3rem" : "1.6rem",
              }}
            >
              {total.amount}
            </Typography>
          </Box>
        ) : (
          children
        )}
      </Box>
    </Paper>
  );
};

export default CalculationSummary;
